import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CalenderIcon from '@/assets/icons/calendar.svg?react';
import PolicyIcon from '@/assets/icons/policy.svg?react';
import RouteIcon from '@/assets/icons/route-filled.svg?react';
import SlowSectionIcon from '@/assets/icons/slow-section.svg?react';
import SpeedIcon from '@/assets/icons/speed.svg?react';
import StationListIcon from '@/assets/icons/station-list.svg?react';
import { InfoRow } from '@/components/drawers/item-drawer/slow-section-drawer/info-row';
import { CollapsibleText } from '@/components/ui/collapsible-text';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import useBreakpoint from '@/hooks/use-break-point';
import { useContentHeight } from '@/hooks/use-content-height';
import useGetSlowSections from '@/hooks/use-get-slow-sections';
import { cn } from '@/lib/utils';
import { formatDistance } from '@/utils/helpers/format-slow-section-distance';

interface SlowSectionDrawerProps {
  slowSectionId: string | null;
  onClose: () => void;
  isToastVisible?: boolean;
  onHeightChange?: (height: number) => void;
}

const SlowSectionDrawer: FC<SlowSectionDrawerProps> = ({
  slowSectionId,
  onClose,
  isToastVisible,
  onHeightChange,
}) => {
  const isMobile = useBreakpoint('mobile');
  const { t } = useTranslation();
  const { data: slowSections = [] } = useGetSlowSections();
  const isOpen = Boolean(slowSectionId);

  const slowSection = useMemo(
    () => slowSections.find((section) => section.id === slowSectionId),
    [slowSections, slowSectionId]
  );

  const [displaySection, setDisplaySection] = useState(slowSection);

  const contentRef = useContentHeight({ onHeightChange, enabled: isOpen });

  useEffect(() => {
    if (slowSection) {
      setDisplaySection(slowSection);
    }
  }, [slowSection]);

  const {
    startStop,
    endStop,
    maxSpeed,
    kmTo,
    kmFrom,
    length,
    validFrom,
    validTo,
    remark,
    speedSectionName,
  } = displaySection ?? {};

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()} modal={false}>
      <SheetContent
        ref={contentRef}
        onInteractOutside={(e) => e.preventDefault()}
        side="bottom"
        className={cn(
          'fixed grid grid-rows-[auto_minmax(0,1fr)] gap-3 p-4 shadow-none border-0 z-50 transition-all duration-300 ease-in-out max-h-90 overflow-hidden',
          {
            'left-3 bottom-16 max-w-[365px] rounded-lg': !isMobile,
            'left-0 bottom-13 min-w-dvw rounded-none': isMobile,
            'bottom-[100px]': isToastVisible && !isMobile,
            'bottom-[92px]': isToastVisible && isMobile,
          }
        )}
      >
        {displaySection && (
          <>
            <SheetHeader className="mb-1 text-left p-0">
              <SheetTitle className="font-semibold text-base text-foreground">
                {t('map.slow_section.title')}
              </SheetTitle>
            </SheetHeader>
            <ScrollArea className="min-h-0" scrollBarClassName="w-0">
              <div className="flex flex-col gap-3">
                <InfoRow icon={<StationListIcon aria-hidden />}>
                  <span className="font-semibold">{startStop?.designationOfficial}</span>
                  <span className="mx-1">{t('common.to')}</span>
                  <span className="font-semibold">{endStop?.designationOfficial}</span>
                </InfoRow>

                <InfoRow icon={<SlowSectionIcon aria-hidden />}>
                  {speedSectionName || t('common.not_available')}
                </InfoRow>

                <InfoRow icon={<RouteIcon aria-hidden />}>
                  <span className="font-semibold ">{formatDistance(kmFrom)}</span>
                  <span className="mx-1">{t('common.to')}</span>
                  <span className="font-semibold">{formatDistance(kmTo)}</span>
                  {length !== null && <span className="font-semibold">{` / ${length} m`}</span>}
                </InfoRow>

                <InfoRow icon={<SpeedIcon aria-hidden />}>
                  {t('map.slow_section.speed', { speed: maxSpeed ?? '-' })}
                </InfoRow>

                <InfoRow icon={<CalenderIcon aria-hidden />}>
                  {`${validFrom ?? '-'} ${t('common.until')} ${validTo ?? '-'}`}
                </InfoRow>

                <InfoRow icon={<PolicyIcon aria-hidden />} className="items-start">
                  <CollapsibleText
                    key={slowSectionId}
                    text={remark || t('common.not_available')}
                    collapsedLines={2}
                    lineHeight="3rem"
                  />
                </InfoRow>
              </div>
            </ScrollArea>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SlowSectionDrawer;
